import { Cart } from "@/utils/types/types";

export const NON_ZERO_SHIPPING_SMALLER_SIDE_THRESHOLD = 21;
export const NON_ZERO_SHIPPING_LARGER_SIDE_THRESHOLD = 27;
export const OVERSIZED_SMALLER_SIDE_THRESHOLD = 45;
export const OVERSIZED_LARGER_SIDE_THRESHOLD = 54;

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const cleaned = value.replace(/[,"]/g, "").trim();
    const parsed = Number.parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

// FIX 2026-07-20 (PIN #12b): mirror of the backend's normalizeTreatmentType
// (stripe.ts, PIN #2). Real product data reads "no frame, matted" for
// mat-only pieces — the word "frame" inside "NO FRAME" defeated the old
// exact-match check here, so mat-onlys counted like frames and this
// heuristic quote-gated real mat carts the backend prices happily.
//   "no frame"         -> print (rolled; nothing left after the strip)
//   "no frame, matted" -> "matted" -> MAT-ONLY (stacks; never a quote trigger
//                         on its own — matches classifyCartPieces server-side)
//   anything else      -> framed (straight fit, matted and framed, canvas...)
const normalizeTreatment = (
  rawType: unknown
): { treatment: string; isPrint: boolean } => {
  const full = String(rawType || "")
    .toLowerCase()
    .trim();
  const treatment = full
    .replace(/no\s*frame/g, " ")
    .replace(/[,;/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return { treatment, isPrint: /no\s*frame/.test(full) && treatment === "" };
};

const parseOverallDimensionsFromText = (
  value: unknown
): { width: number; height: number } | null => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;

  const overallMatch = text.match(
    /overall dimensions[^0-9]*(\d+(?:\.\d+)?)[\s"]*[x×][\s"]*(\d+(?:\.\d+)?)/i
  );
  if (overallMatch) {
    return {
      width: toNumber(overallMatch[1]),
      height: toNumber(overallMatch[2]),
    };
  }

  const genericMatch = text.match(
    /(\d+(?:\.\d+)?)[\s"]*[x×][\s"]*(\d+(?:\.\d+)?)/i
  );
  if (genericMatch) {
    return {
      width: toNumber(genericMatch[1]),
      height: toNumber(genericMatch[2]),
    };
  }

  return null;
};

const getItemOverallDimensions = (
  itemJson: Record<string, unknown>
): { width: number; height: number; source: string } => {
  const sizing = toRecord(itemJson.sizing);
  const dimensions = toRecord(sizing.dimensions);

  const explicitOverallWidth = toNumber(dimensions.overallWidth || 0);
  const explicitOverallHeight = toNumber(dimensions.overallHeight || 0);
  if (explicitOverallWidth > 0 && explicitOverallHeight > 0) {
    return {
      width: explicitOverallWidth,
      height: explicitOverallHeight,
      source: "sizing.dimensions.overallWidth/overallHeight",
    };
  }

  const cartData = toRecord(itemJson.cartData);
  const staticFieldsToScan = [
    cartData.staticField1,
    cartData.staticField2,
    cartData.staticField3,
    cartData.staticField4,
  ];
  for (const field of staticFieldsToScan) {
    const parsed = parseOverallDimensionsFromText(field);
    if (parsed && parsed.width > 0 && parsed.height > 0) {
      return { ...parsed, source: "cartData.staticField*" };
    }
  }

  return { width: 0, height: 0, source: "missing-overall-dimensions" };
};

type ItemHeuristicDebug = {
  lineItemId: string;
  quantity: number;
  treatmentType: string;
  normalizedTreatment: string;
  excludeFromShipping: boolean;
  dimensionSource: string;
  width: number;
  height: number;
  smaller: number;
  larger: number;
  isPrint: boolean;
  isMatOnly: boolean;
  qualifiesNonZero: boolean;
  qualifiesOversized: boolean;
  /** Framed pieces that cross the non-zero shipping thresholds. */
  nonZeroContribution: number;
  /** ALL framed pieces with dimensions (any size). */
  framedContribution: number;
  /** Mat-only pieces (stackable — never a multi trigger on their own). */
  matOnlyContribution: number;
  skippedReason?: string;
};

export const getHeuristicDebug = (cart?: Cart | null): ItemHeuristicDebug[] => {
  if (!cart?.lineItems?.length) return [];

  return cart.lineItems.map(lineItem => {
    const itemJson = toRecord(lineItem.itemJson);
    const excludeFromShipping = Boolean(itemJson.excludeFromShipping);
    const quantity = Math.max(0, Number(lineItem.quantity || 0));
    const treatmentOptions = toRecord(itemJson.treatmentOptions);
    const treatmentType = String(treatmentOptions.type || "")
      .toLowerCase()
      .trim();
    const { treatment: normalizedTreatment, isPrint } =
      normalizeTreatment(treatmentType);
    const isMatOnly =
      !isPrint &&
      normalizedTreatment.includes("mat") &&
      !normalizedTreatment.includes("frame");

    const { width, height, source } = getItemOverallDimensions(itemJson);
    const smaller = Math.min(width, height);
    const larger = Math.max(width, height);

    const hasDimensions = width > 0 && height > 0;
    const isCountable =
      !excludeFromShipping && !isPrint && quantity > 0 && hasDimensions;
    const isFramed = isCountable && !isMatOnly;
    const qualifiesNonZero =
      isFramed &&
      (smaller > NON_ZERO_SHIPPING_SMALLER_SIDE_THRESHOLD ||
        larger > NON_ZERO_SHIPPING_LARGER_SIDE_THRESHOLD);
    // Oversized keys off ANY non-print piece (mats included) — the backend's
    // exceeds-table check uses the cart's outer dims the same way.
    const qualifiesOversized =
      isCountable &&
      smaller > OVERSIZED_SMALLER_SIDE_THRESHOLD &&
      larger > OVERSIZED_LARGER_SIDE_THRESHOLD;

    let skippedReason: string | undefined;
    if (excludeFromShipping) skippedReason = "excludeFromShipping";
    else if (quantity <= 0) skippedReason = "quantity<=0";
    else if (isPrint) skippedReason = "treatment=no frame (print)";
    else if (!hasDimensions) skippedReason = "missing-dimensions";
    else if (isMatOnly) skippedReason = "mat-only (stacks; not a multi trigger)";
    else if (!qualifiesNonZero) skippedReason = "below-non-zero-threshold";

    return {
      lineItemId: lineItem.id,
      quantity,
      treatmentType,
      normalizedTreatment,
      excludeFromShipping,
      dimensionSource: source,
      width,
      height,
      smaller,
      larger,
      isPrint,
      isMatOnly,
      qualifiesNonZero,
      qualifiesOversized,
      nonZeroContribution: qualifiesNonZero ? quantity : 0,
      framedContribution: isFramed ? quantity : 0,
      matOnlyContribution: isCountable && isMatOnly ? quantity : 0,
      skippedReason,
    };
  });
};

export const countItemsWithNonZeroShippingByDimensions = (
  cart?: Cart | null
) => {
  return getHeuristicDebug(cart).reduce(
    (count, item) => count + item.nonZeroContribution,
    0
  );
};

// FIX 2026-07-20 (PIN #12b): mirror the backend multi-piece quote triggers
// (classifyCartPieces, stripe.ts):
//   framed >= 2 with at least one past the small-flat-ish thresholds, OR
//   framed >= 1 with MORE than 3 mat-onlys riding along.
// Mat-only carts NEVER trigger on their own — mats stack into one package at
// any quantity, and the backend auto-prices them (they were wrongly
// quote-gated here before this fix).
export const hasMultipleCalculatedItemsByDimensions = (cart?: Cart | null) => {
  const debug = getHeuristicDebug(cart);
  const framedTotal = debug.reduce(
    (count, item) => count + item.framedContribution,
    0
  );
  const framedNonZero = debug.reduce(
    (count, item) => count + item.nonZeroContribution,
    0
  );
  const matOnlyTotal = debug.reduce(
    (count, item) => count + item.matOnlyContribution,
    0
  );
  return (
    (framedTotal >= 2 && framedNonZero >= 1) ||
    (framedTotal >= 1 && matOnlyTotal > 3)
  );
};

export const hasOversizedItemsByDimensions = (cart?: Cart | null) => {
  return getHeuristicDebug(cart).some(item => item.qualifiesOversized);
};

export const getPreemptiveShippingTypeByDimensions = (
  cart?: Cart | null
): "oversized" | "multiple-calculated" | null => {
  if (hasOversizedItemsByDimensions(cart)) return "oversized";
  if (hasMultipleCalculatedItemsByDimensions(cart))
    return "multiple-calculated";
  return null;
};
